/**
 * « Faire signer » — M1 (retour Manon, mi-parcours).
 *
 * Bouton réutilisable partout où un document interne se signe : remise de
 * clé, état des lieux, sortie d'archive… Deux façons :
 *   · « Signer ici, maintenant » : la tablette passe au signataire ;
 *   · « Copier le lien » : à envoyer (le lien expire sous 7 jours).
 * Affiche l'état des signatures déjà demandées et ouvre le PDF signé.
 * Ajouter un endroit = poser <FaireSignerButton documentType=… documentId=… />.
 */
import { useCallback, useEffect, useState } from 'react';
import { PenLine } from 'lucide-react';
import { apiFetch, ApiException } from '@/lib/api';

type TypeSignable = 'remise_cles' | 'etat_des_lieux' | 'sortie_archive';
interface Sig { id: string; url: string; statut: string; signataireNom: string; signeAt: string | null }

export function FaireSignerButton({ documentType, documentId, signataireParDefaut = '' }: {
  documentType: TypeSignable; documentId: string; signataireParDefaut?: string;
}) {
  const [sigs, setSigs] = useState<Sig[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState(signataireParDefaut);
  const [err, setErr] = useState<string | null>(null);
  const [copie, setCopie] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await apiFetch<{ signatures: Sig[] }>(`/api/signatures-simples?documentType=${documentType}&documentId=${documentId}`);
      setSigs(r.signatures);
    } catch { setSigs([]); }
  }, [documentType, documentId]);
  useEffect(() => { void charger(); }, [charger]);

  async function demander(): Promise<Sig | null> {
    setErr(null);
    try {
      const d = await apiFetch<Sig>('/api/signatures-simples', {
        method: 'POST', body: JSON.stringify({ documentType, documentId, signataireNom: nom.trim() }),
      });
      await charger();
      return d;
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Demande impossible'); return null; }
  }

  async function ouvrirPdf(id: string) {
    try { const { url } = await apiFetch<{ url: string }>(`/api/signatures-simples/${id}/pdf`); window.open(url, '_blank', 'noopener'); }
    catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'PDF indisponible'); }
  }

  const signee = sigs.find((s) => s.statut === 'signe');
  return (
    <span className="inline-flex flex-col gap-1 text-xs">
      {signee ? (
        <button type="button" className="inline-flex items-center gap-1 text-success hover:underline" onClick={() => void ouvrirPdf(signee.id)}>
          <PenLine className="h-3.5 w-3.5" aria-hidden="true" /> Signé par {signee.signataireNom}{signee.signeAt ? ` le ${new Date(signee.signeAt).toLocaleDateString('fr-FR')}` : ''}
        </button>
      ) : !ouvert ? (
        <button type="button" className="inline-flex items-center gap-1 text-accent hover:underline" onClick={() => setOuvert(true)}>
          <PenLine className="h-3.5 w-3.5" aria-hidden="true" /> Faire signer{sigs.some((s) => s.statut === 'en_attente') ? ' (en attente)' : ''}
        </button>
      ) : (
        <span className="flex flex-wrap items-center gap-1.5">
          <input className="h-7 rounded-r-md border border-border bg-surface px-2" aria-label="Nom du signataire"
            placeholder="Nom du signataire" value={nom} maxLength={160} onChange={(e) => setNom(e.target.value)} />
          <button type="button" disabled={nom.trim().length < 2} className="font-medium text-accent hover:underline disabled:opacity-40"
            onClick={() => void demander().then((d) => { if (d) window.location.assign(new URL(d.url).pathname); })}>
            Signer ici, maintenant
          </button>
          <button type="button" disabled={nom.trim().length < 2} className="text-accent hover:underline disabled:opacity-40"
            onClick={() => void demander().then(async (d) => { if (d) { await navigator.clipboard?.writeText(d.url); setCopie(true); } })}>
            {copie ? 'Lien copié ✓' : 'Copier le lien'}
          </button>
          <button type="button" className="text-ink-subtle hover:underline" onClick={() => setOuvert(false)}>Annuler</button>
        </span>
      )}
      {err && <span className="text-urgent" role="alert">{err}</span>}
    </span>
  );
}
